import React from 'react';
import { AlertTriangle, Clock, Users, DollarSign, CalendarClock } from 'lucide-react';
import type { AgingSummary } from '../../../utils/outstandingUtils';
import { fmtUSD } from '../../../utils/outstandingUtils';
import type { DsoMetrics } from '../../../hooks/useOutstandingCollections';

interface KpiStripProps {
  summary: AgingSummary;
  dso: DsoMetrics | null;
}

export const KpiStrip: React.FC<KpiStripProps> = ({ summary, dso }) => {
  const tiles = [
    {
      label: 'Total Outstanding',
      value: fmtUSD(summary.grandTotal),
      sub: `${summary.totalInvoices} invoices`,
      accent: 'bg-red-500',
      textColor: 'text-red-700',
      bg: 'bg-red-50',
      icon: <DollarSign size={16} className="text-red-500" />,
    },
    {
      label: 'Critical Exposure',
      value: fmtUSD(summary.criticalOutstanding),
      sub: `${summary.criticalRiskPct.toFixed(1)}% of total`,
      accent: 'bg-orange-500',
      textColor: 'text-orange-700',
      bg: 'bg-orange-50',
      icon: <AlertTriangle size={16} className="text-orange-500" />,
    },
    {
      label: 'Overdue Amount',
      value: fmtUSD(summary.overdueOutstanding),
      sub: `${summary.overduePct.toFixed(1)}% overdue`,
      accent: 'bg-amber-500',
      textColor: 'text-amber-700',
      bg: 'bg-amber-50',
      icon: <Clock size={16} className="text-amber-500" />,
    },
    {
      label: 'Unique Clients',
      value: String(summary.totalClients),
      sub: 'with outstanding invoices',
      accent: 'bg-blue-500',
      textColor: 'text-blue-700',
      bg: 'bg-blue-50',
      icon: <Users size={16} className="text-blue-500" />,
    },
    {
      label: 'DSO (est.)',
      value:
        dso?.days != null && Number.isFinite(dso.days)
          ? `${Math.round(dso.days)} days`
          : '—',
      sub:
        dso != null && dso.t12InvoicedUsd > 0
          ? `AR ÷ T12 invoiced (${fmtUSD(dso.t12InvoicedUsd)}) × 365`
          : 'Need T12 invoiced from global trends',
      accent: 'bg-indigo-500',
      textColor: 'text-indigo-700',
      bg: 'bg-indigo-50',
      icon: <CalendarClock size={16} className="text-indigo-500" />,
    },
  ];

  return (
    <div className="space-y-3">
      {/* Alert banner */}
      {summary.isUrgent && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-none" />
          <span className="text-[12px] font-bold text-red-700">URGENT COLLECTIONS ALERT</span>
          <span className="text-[11px] text-red-600 ml-1">
            — {summary.criticalRiskPct.toFixed(1)}% of total outstanding is at critical risk. Immediate action required.
          </span>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {tiles.map(tile => (
          <div key={tile.label} className="bg-white rounded-xl border border-slate-200 shadow-md p-4 flex gap-3 items-start">
            <div className={`w-1 self-stretch rounded-full flex-none ${tile.accent}`} />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{tile.label}</p>
              <p className={`text-xl font-bold tabular-nums leading-tight ${tile.textColor}`}>{tile.value}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{tile.sub}</p>
            </div>
            <div className={`w-8 h-8 rounded-lg ${tile.bg} flex items-center justify-center flex-none`}>
              {tile.icon}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KpiStrip;
